import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { clearGateCache, type BackendGateClient } from './backend-gates';

// Keeps the backend gate client (backend-gates.ts) in step with Clerk: it
// seeds the client from the local cache before the first paint, then fetches
// GET /api/gates on mount and again on every user change (sign-in, sign-out,
// switch). Nothing here reads consent: every user gets the same gates,
// whatever they chose on the cookie banner.

interface GateSyncProps {
    client: BackendGateClient;
    signedIn: boolean;
    /** The signed-in user's id; null when signed out. */
    userId: string | null;
    getToken: () => Promise<string | null>;
}

function useGateSync({ client, signedIn, userId, getToken }: GateSyncProps): void {
    // Once, before paint, so rolled-out features don't flash off. A signed-out
    // load drops a cache written while signed in, so a sign-out that happened
    // while the app was closed never shows the previous user's gates.
    useLayoutEffect(() => {
        client.seedFromCache(signedIn);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once, from the first render
    }, []);

    const previousUserId = useRef<string | null>(null);
    useEffect(() => {
        // On sign-out the cache still holds the previous user's values: drop
        // it now rather than wait for the anonymous answer, which may fail.
        if (previousUserId.current !== null && userId === null) clearGateCache();
        previousUserId.current = userId;

        // Set when a newer user change (or unmount) takes over while the
        // token is still loading, so a stale user's fetch never starts.
        let superseded = false;
        const sync = async () => {
            let token: string | null = null;
            if (signedIn) {
                try {
                    token = await getToken();
                } catch {
                    // No token: the backend answers with anonymous values.
                }
            }
            // Yield once so the signed-out path, which has no token to await,
            // also gives a synchronous cleanup (StrictMode's mount-unmount-mount)
            // the chance to mark this run superseded before it fetches.
            await Promise.resolve();
            if (superseded) return;
            await client.refresh({ signedIn, token });
        };
        // refresh() settles failed fetches itself; it rejects only when a
        // values_updated listener throws (after the values were applied, but
        // later listeners are skipped), which is a bug: report it, don't hide it.
        sync().catch((error: unknown) => {
            console.error('[gates] a values_updated listener threw', error);
        });
        return () => { superseded = true; };
        // getToken is deliberately not a dep: a fresh identity is not a user change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, signedIn, userId]);
}

/**
 * Feeds auth state into the backend gate client. Split from FeatureGateSync
 * so tests drive it with plain props (bun's mock.module is process-global).
 */
export function GateSync(props: GateSyncProps): null {
    useGateSync(props);
    return null;
}

/** Mounted once in src/index.tsx, inside <ClerkLoaded> (so auth is loaded) and
 *  outside AnalyticsProvider (so gates never depend on analytics consent). */
export function FeatureGateSync({ client }: { client: BackendGateClient }): null {
    const { isSignedIn, userId, getToken } = useAuth();
    const signedIn = isSignedIn === true;
    useGateSync({ client, signedIn, userId: signedIn ? (userId ?? null) : null, getToken });
    return null;
}
